import { BiChevronRight } from 'react-icons/bi';
import { FiLock } from 'react-icons/fi';
import { TbNotebook } from 'react-icons/tb';

import { Card, StatusPill } from '@/components';
import { EmptyPage } from '@/components/EmptyPage';
import type { AccessVerdict } from '@/permissions/plan-access';
import { useAccessGate } from '@/permissions/useAccessGate';
import { usePermissions } from '@/permissions/usePermissions';

import { LOG_BOOKS, logBookPath, type LogBookRoute } from './logbookRoutes';
import { formatLastWritten, useLogBookStanding } from './logbookStanding';
import s from './LogsIndexPage.module.scss';

/**
 * How current the book is. A book that has genuinely never been written says so
 * in words and is set apart from the dates, rather than showing a dash or an
 * empty slot beside four filled siblings.
 */
const standingNote = (latest?: string) =>
  latest ? (
    `Last written ${formatLastWritten(latest).toLowerCase()}`
  ) : (
    <span className={s.never}>Never written</span>
  );

/**
 * One book. Its own component because each reads from its own store — see
 * `useLogBookStanding` — and because a book the plan withholds must not fetch at
 * all, which is why the locked card below is a different component rather than a
 * branch inside this one.
 */
const LogBookCard = ({ book }: { book: LogBookRoute }) => {
  const { loading, latest, count } = useLogBookStanding(book);

  return (
    <li>
      {/* The card is the link, so the whole thing is one target with one tab stop
          — and it opens today's page, not the book's history: writing in it is
          what you came for, and the history is one control away inside. */}
      <Card as="article" className={s.card} to={logBookPath(book)} ariaLabel={book.label}>
        <div className={s.head}>
          <span className={s.icon} aria-hidden="true">
            {book.icon}
          </span>
          <h2 className={`headline-small ${s.name}`}>{book.label}</h2>
          <BiChevronRight className={s.chevron} aria-hidden="true" />
        </div>

        <p className={`body-small ${s.what}`}>{book.subhead}</p>

        <div className={s.footer}>
          {/* Never a claim about this vessel's record while the store is loading —
              every wording here would be one. */}
          <span className={`body-small ${s.note}`}>
            {loading ? <span className={s.skeleton} aria-hidden="true" /> : standingNote(latest)}
          </span>
          <span className={`body-small ${s.count}`}>
            {loading ? '' : `${count} ${count === 1 ? 'entry' : 'entries'}`}
          </span>
        </div>
      </Card>
    </li>
  );
};

/**
 * A book the vessel's plan does not carry. Only the owner ever sees one — see
 * `AccessVerdict` — and it still opens, onto the placeholder explaining why.
 * Nothing is fetched for it: every request behind the card would be a 403.
 */
const LockedLogBookCard = ({ book }: { book: LogBookRoute }) => (
  <li>
    <Card as="article" className={s.card} to={logBookPath(book)} ariaLabel={book.label}>
      <div className={s.head}>
        <span className={s.icon} aria-hidden="true">
          <FiLock color="var(--color-sun-flare)" />
        </span>
        <h2 className={`headline-small ${s.name}`}>{book.label}</h2>
        <BiChevronRight className={s.chevron} aria-hidden="true" />
      </div>

      <p className={`body-small ${s.what}`}>{book.subhead}</p>

      <div className={s.footer}>
        <span className={`body-small ${s.note}`}>
          <StatusPill label="Not on this plan" tone="neutral" />
        </span>
      </div>
    </Card>
  </li>
);

/**
 * Logs — the five books, and where each one stands.
 *
 * The page exists because the five logs are five separate documents, not five
 * views of one. Choosing between them is a step, so it gets a place: the
 * breadcrumb can then say `Manage › Logs › Oil Log` and its switcher can carry
 * the other four, where the page used to spend a tab strip on the same job.
 *
 * Each card also says when its book was last written, which is the one question
 * that spans all five and cannot be answered from inside any of them.
 */
const LogsIndexPage = () => {
  const { rights } = usePermissions();
  const { verdictFor } = useAccessGate();

  const books: { book: LogBookRoute; verdict: AccessVerdict }[] = LOG_BOOKS.map((book) => ({
    book,
    verdict: verdictFor(book.permission),
  })).filter(({ verdict }) => verdict !== 'hidden');

  if (books.length === 0) {
    return (
      <EmptyPage
        icon={<TbNotebook size={64} />}
        title="No logs available"
        desc={
          rights.size === 0
            ? 'Checking what this vessel gives you access to…'
            : 'Your role on this vessel has no logbook access. Please ask the owner or captain to grant it in Roles & Permissions.'
        }
      />
    );
  }

  return (
    <section className={s.page} aria-label="Logs">
      <ul className={s.grid}>
        {books.map(({ book, verdict }) =>
          verdict === 'open' ? (
            <LogBookCard key={book.path} book={book} />
          ) : (
            <LockedLogBookCard key={book.path} book={book} />
          ),
        )}
      </ul>
    </section>
  );
};

export default LogsIndexPage;
